// ── §5.1e — WHAT EACH PROVIDER CALLS THE THING THE EVIDENCE SITS IN ────────────────────────────
//
// ⚠ THIS EXISTS BECAUSE FOUR SENTENCES SAID "LIBRARY" TO A BOX TENANT.
//
//   "Library reachable", "Library access lost", "Moved in the library since capture" and "No longer
//   in the library" are SharePoint's vocabulary, and they were rendered verbatim for Box bindings —
//   where there is no library, only a folder. In a compliance product the surface is the record a
//   reader acts on, so a confidently wrong noun is not cosmetic: it describes somewhere the tenant
//   does not keep their evidence.
//
// ⚠ A TABLE, NOT A BRANCH AT THE RENDER SITE (D-PROVISION-REPOSITORY-UI-PROVIDER-AGNOSTIC-1). The
//   panel must not be able to express a provider-specific thought; it passes the binding's repo_kind
//   to a derive function and renders whatever comes back, exactly as it already does with
//   `integrityShortFor(artifact.hash_algorithm)`. A conditional in the panel is invisible until it
//   is wrong — the next provider is added to the API, the panel keeps its SharePoint branch, and a
//   tenant sees the wrong word again.
//
// ⚠ AND SHAREPOINT'S WORDS ARE DERIVED FROM THIS TABLE, NOT RE-HARDCODED ALONGSIDE IT. That is the
//   same guarantee `INTEGRITY_SHORT = integrityShortFor(SHAREPOINT_HASH_ALGORITHM)` carries: if the
//   derivation drifts, the SharePoint regression fails LOUDLY instead of the Box copy quietly
//   diverging from a second copy of the shipped strings.

/** The provider whose wording shipped first, and against which byte-identity is asserted. */
export const SHAREPOINT_REPO_KIND = 'sharepoint';

/**
 * The container noun per provider — the word for the thing a control's evidence sits in.
 *
 * ⓘ RELATED BUT NOT THE SAME as `containerMeans` in the API's evidence-repository-adapter, which is
 *   documentation for maintainers and is never emitted. This one is rendered to tenants. The key
 *   sets must stay in step, which `KNOWN_REPO_KINDS` below is pinned to catch.
 */
const CONTAINER_NOUN: Record<string, string> = {
  sharepoint: 'library',
  // ⓘ Box has no library and no drive: a control's evidence sits in a folder.
  box: 'folder',
};

/**
 * ⚠ PINNED SO A NEW PROVIDER CANNOT BE HALF-ADDED. Adding a row above without extending this list
 *   fails; so does adding a provider to the API registry and forgetting this table, because the
 *   test that reads this list names the other file. A provider added in one place only is how a
 *   tenant ends up reading another provider's vocabulary.
 */
export const KNOWN_REPO_KINDS = ['sharepoint', 'box'] as const;

/**
 * The noun for this provider's container.
 *
 * ⚠ THE UNKNOWN CASE IS NEUTRAL AND TRUE, NEVER SHAREPOINT-ASSUMED. Falling back to "library" would
 *   put a confident wrong word in front of a tenant — the same mistake as defaulting an unknown
 *   provider name to "Microsoft SharePoint", which `providerDisplayName` already refuses. "Folder"
 *   would be a guess too. "Location" claims nothing about how the provider organises anything, and
 *   reads correctly in every sentence below.
 */
export function containerNoun(repoKind: string | null | undefined): string {
  if (!repoKind) return 'location';
  return CONTAINER_NOUN[repoKind] ?? 'location';
}

/** Capitalised for sentence-initial use — one helper, so no caller hand-rolls a capitalisation. */
export function containerNounCapitalised(repoKind: string | null | undefined): string {
  const n = containerNoun(repoKind);
  return n.charAt(0).toUpperCase() + n.slice(1);
}
